import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'
import { AppProviders } from '@/providers/AppProviders'
import { router } from '@/router'
import { env } from '@/lib/env'
import { initSentry } from '@/lib/sentry-init'
import { consumeOIDCTokenFragment } from '@/lib/oidc-token-handoff'
import { installGlobalChunkErrorListeners } from '@/lib/chunk-error-handler'
import './index.css'
// Third-party CSS — imported once at the entry point.
import 'leaflet/dist/leaflet.css'
import '@xyflow/react/dist/style.css'
import '@/lib/leaflet-setup'

async function enableMocks(): Promise<void> {
  // `import.meta.env.DEV` is intentionally NOT part of the gate.
  // E2E suites run against a production build (`vite preview`) so
  // chunk loading isn't a parallel-worker bottleneck — and that build
  // needs MSW to come up the same way the dev server does. The only
  // signal that matters is whether the operator opted into mocks via
  // VITE_USE_MOCKS at build/serve time.
  if (!env.VITE_USE_MOCKS) return
  const { worker } = await import('@/api/mocks/browser')
  await worker.start({ onUnhandledRequest: 'bypass' })
}

// Unregister any stale service worker registered by a prior MSW run.
// Service workers persist across dev sessions; when VITE_USE_MOCKS
// flips back to false, the SW continues to intercept requests in the
// background, which (a) silently breaks same-origin navigations like
// the SSO <a href="/api/...">  click, (b) returns mocked responses
// for endpoints whose mock handler was deleted between sessions.
//
// Skip the cleanup only when mocks are explicitly enabled — otherwise
// always unregister, every page load, regardless of dev/prod.
async function killStaleServiceWorkers(): Promise<void> {
  if (env.VITE_USE_MOCKS) return
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
  try {
    const regs = await navigator.serviceWorker.getRegistrations()
    if (regs.length === 0) return
    await Promise.all(regs.map((r) => r.unregister()))
     
    console.info(`[boot] unregistered ${regs.length} stale service worker(s)`)
  } catch {
    /* best-effort; never fail the boot path on this */
  }
}

// Initialise Sentry first so any error during the OIDC fragment
// handoff or the boot promise chain is captured. No-op when
// VITE_SENTRY_DSN is unset (dev / CI / demo).
initSentry()

// Register global window-level listeners for stale-chunk 404s that
// bypass the per-lazy safeLazy() wrapper (e.g. sub-routes lazily
// composed inside a feature file). Idempotent. Must run before any
// lazy() factory can fire, so we call it immediately — synchronously
// after Sentry is wired so errors are also captured by the SDK.
installGlobalChunkErrorListeners()

// Run the OIDC fragment handoff synchronously BEFORE React mounts.
// If we land on /some/path#access_token=..., we want the tokens in
// localStorage before the very first render of RequireAuth so the
// /me probe carries the Bearer header on the first call (no
// redirect-to-login bounce).
consumeOIDCTokenFragment()

void Promise.all([killStaleServiceWorkers(), enableMocks()]).then(() => {
  const rootEl = document.getElementById('root')
  if (!rootEl) throw new Error('Root element #root not found')
  createRoot(rootEl).render(
    <StrictMode>
      <AppProviders>
        <RouterProvider router={router} />
      </AppProviders>
    </StrictMode>,
  )
})
